import React, { useMemo } from 'react';

import { Button, IconButton, Portal, Typography, makeStyles } from '@material-ui/core';
import PauseIcon from '@material-ui/icons/Pause';
import PlayArrowIcon from '@material-ui/icons/PlayArrow';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import clsx from 'clsx';

import { formatRemainingTime, isSheetsTimerOverLimit } from 'constants/dictionaries/sheetsTimer';
import { FEATURE_FLAGS } from 'constants/dictionaries/featureFlags';
import LAYOUT_SIZES from 'constants/styles/LAYOUT_SIZES';
import { useFeatureFlag } from 'hooks/useFeatureFlags/useFeatureFlags';
import useFeatureChecker from 'services/common/useFeatureChecker/useFeatureChecker';
import useSheetsStore from 'storages/sheets/sheets';
import useSheetsTimerStore from 'storages/sheetsTimer/sheetsTimer';

const useStyles = makeStyles(theme => ({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: 'none',
    minWidth: '200px',
    marginRight: theme.spacing(4),
    padding: theme.spacing(1, 2),
    transition: 'height .3s ease, padding .3s ease',
    height: ({ isStuck }: { isStuck: boolean }) => (isStuck ? '50px' : '95px'),
    [theme.breakpoints.down('md')]: {
      minWidth: 'auto',
      marginRight: 0,
      height: 'auto',
      padding: theme.spacing(0.5, 1),
    },
  },
  content: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  label: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
  time: {
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.2,
  },
  overLimit: {
    color: theme.palette.warning.dark,
  },
  controlButton: {
    padding: theme.spacing(0.5),
  },
  startButton: {
    marginTop: theme.spacing(0.5),
    textTransform: 'none',
    fontWeight: 600,
  },
  showTimeButton: {
    textTransform: 'none',
    fontSize: '0.8125rem',
    fontWeight: 500,
    padding: theme.spacing(0.25, 1),
    minWidth: 0,
  },
  pomoceWrapper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    marginTop: theme.spacing(1),
    padding: theme.spacing(1, 2),
    backgroundColor: 'transparent',
    border: `1px solid ${theme.palette.primary.light}`,
    borderRadius: theme.shape.borderRadius,
  },
  pomoceWrapperCompact: {
    padding: theme.spacing(0.75, 2),
  },
  mobileFixedWrapper: {
    position: 'fixed',
    bottom: LAYOUT_SIZES.FOOTER.sm,
    left: 0,
    right: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(0.75, 2),
    backgroundColor: theme.palette.common.white,
    boxShadow: theme.shadows[2],
    zIndex: 101,
  },
  mobileFixedWrapperCompact: {
    padding: theme.spacing(0.5, 2),
  },
}));

export const SHEETS_TIMER_MOBILE_BAR_HEIGHT = 36;
export const SHEETS_TIMER_MOBILE_BAR_HEIGHT_COMPACT = 28;

export const useIsSheetsTimerConfigured = (): boolean => {
  const isEnabled = useFeatureFlag(FEATURE_FLAGS.SHEETS_TIMER);
  const { isSheet } = useFeatureChecker();
  const { fetched } = useSheetsStore();
  const { durationMinutes, activeSheetKey } = useSheetsTimerStore();

  return isEnabled && isSheet && fetched && durationMinutes != null && activeSheetKey != null;
};

export const useIsSheetsTimerActive = (): boolean => {
  const isConfigured = useIsSheetsTimerConfigured();
  const { isStarted } = useSheetsTimerStore();

  return isConfigured && isStarted;
};

export const useIsSheetsTimerBarVisible = (): boolean => {
  const isConfigured = useIsSheetsTimerConfigured();
  const { isStarted, isDisplayVisible } = useSheetsTimerStore();

  if (!isConfigured) {
    return false;
  }

  if (!isStarted) {
    return true;
  }

  return isDisplayVisible;
};

export const useSheetsTimerMobileBarHeight = (): number => {
  const isConfigured = useIsSheetsTimerConfigured();
  const { isStarted, isDisplayVisible } = useSheetsTimerStore();

  if (!isConfigured) {
    return 0;
  }

  if (isStarted && !isDisplayVisible) {
    return SHEETS_TIMER_MOBILE_BAR_HEIGHT_COMPACT;
  }

  return SHEETS_TIMER_MOBILE_BAR_HEIGHT;
};

type Props = {
  isStuck?: boolean;
  variant?: 'desktop' | 'pomoce' | 'mobile-fixed';
};

type TimerContentProps = {
  formattedTime: string;
  isOverLimit: boolean;
  isRunning: boolean;
  onPause: () => void;
  onResume: () => void;
  onHide: () => void;
  classes: ReturnType<typeof useStyles>;
  labelLayout?: 'inline' | 'stacked';
};

const TimerContent: React.FC<TimerContentProps> = ({
  formattedTime,
  isOverLimit,
  isRunning,
  onPause,
  onResume,
  onHide,
  classes,
  labelLayout = 'stacked',
}) => {
  const controlButton = isRunning ? (
    <IconButton
      aria-label='Wstrzymaj odliczanie'
      className={classes.controlButton}
      color='primary'
      onClick={onPause}
      size='small'
    >
      <PauseIcon fontSize='small' />
    </IconButton>
  ) : (
    <IconButton
      aria-label='Wznów odliczanie'
      className={classes.controlButton}
      color='primary'
      onClick={onResume}
      size='small'
    >
      <PlayArrowIcon fontSize='small' />
    </IconButton>
  );

  if (labelLayout === 'inline') {
    return (
      <>
        <Typography className={classes.label} component='span'>
          Pozostały czas:
        </Typography>
        <div className={classes.content}>
          <Typography className={clsx(classes.time, isOverLimit && classes.overLimit)} color='primary' component='span'>
            {formattedTime}
          </Typography>
          {controlButton}
          <IconButton
            aria-label='Schowaj odliczanie'
            className={classes.controlButton}
            color='primary'
            onClick={onHide}
            size='small'
          >
            <VisibilityOffIcon fontSize='small' />
          </IconButton>
        </div>
      </>
    );
  }

  return (
    <>
      <Typography className={classes.label} component='span'>
        Pozostały czas
      </Typography>
      <div className={classes.content}>
        <Typography className={clsx(classes.time, isOverLimit && classes.overLimit)} color='primary' component='span'>
          {formattedTime}
        </Typography>
        {controlButton}
        <IconButton
          aria-label='Schowaj odliczanie'
          className={classes.controlButton}
          color='primary'
          onClick={onHide}
          size='small'
        >
          <VisibilityOffIcon fontSize='small' />
        </IconButton>
      </div>
    </>
  );
};

type StartPromptProps = {
  onStart: () => void;
  classes: ReturnType<typeof useStyles>;
};

const StartPrompt: React.FC<StartPromptProps> = ({ onStart, classes }) => (
  <Button className={classes.startButton} color='primary' onClick={onStart} size='small' variant='contained'>
    Rozpocznij
  </Button>
);

type ShowTimePromptProps = {
  onShow: () => void;
  classes: ReturnType<typeof useStyles>;
};

const ShowTimePrompt: React.FC<ShowTimePromptProps> = ({ onShow, classes }) => (
  <Button className={classes.showTimeButton} color='primary' onClick={onShow} size='small'>
    Pokaż czas
  </Button>
);

const SheetsTimerDisplay: React.FC<Props> = ({ isStuck = false, variant = 'desktop' }) => {
  const isConfigured = useIsSheetsTimerConfigured();
  const {
    durationMinutes,
    elapsedSeconds,
    isStarted,
    isDisplayVisible,
    isRunning,
    startCountdown,
    pause,
    resume,
    hideDisplay,
    showDisplay,
  } = useSheetsTimerStore();
  const classes = useStyles({ isStuck });

  const isOverLimit = useMemo(() => isSheetsTimerOverLimit(durationMinutes, elapsedSeconds), [durationMinutes, elapsedSeconds]);

  if (!isConfigured) {
    return null;
  }

  const formattedTime = formatRemainingTime(durationMinutes, elapsedSeconds);
  const isCompact = isStarted && !isDisplayVisible;

  const renderContent = () => {
    if (!isStarted) {
      return <StartPrompt classes={classes} onStart={startCountdown} />;
    }

    if (!isDisplayVisible) {
      return <ShowTimePrompt classes={classes} onShow={showDisplay} />;
    }

    return (
      <TimerContent
        classes={classes}
        formattedTime={formattedTime}
        isOverLimit={isOverLimit}
        isRunning={isRunning}
        labelLayout={variant === 'mobile-fixed' ? 'inline' : 'stacked'}
        onHide={hideDisplay}
        onPause={pause}
        onResume={resume}
      />
    );
  };

  if (variant === 'mobile-fixed') {
    return (
      <Portal>
        <div className={clsx(classes.mobileFixedWrapper, isCompact && classes.mobileFixedWrapperCompact)}>{renderContent()}</div>
      </Portal>
    );
  }

  if (variant === 'pomoce') {
    return (
      <div className={clsx(classes.pomoceWrapper, isCompact && classes.pomoceWrapperCompact)}>{renderContent()}</div>
    );
  }

  return null;
};

export default SheetsTimerDisplay;
